import React from 'react';
import { Box, Typography } from '@mui/material';
import { ActionItem } from '../types';
import { currencyPrefix, itemCurrency } from '../logic';

const BORDER = '#E2E8F0';
const RISK = '#DC2626';
const WARN = '#C2410C';
// The collector's profit figure is the last 30 days, so the daily figure divides by those 30.
const DAYS_IN_WINDOW = 30;

/** Whole dollars with separators and the sign in front, using the given currency prefix. */
function money(v: number, prefix: string): string {
  const n = Math.round(v);
  return `${n < 0 ? '-' : '+'}${prefix}${Math.abs(n).toLocaleString('en-US')}`;
}

/** Unsigned, for a figure that is a size rather than a direction. */
function size(v: number, prefix: string): string {
  return `${prefix}${Math.round(Math.abs(v)).toLocaleString('en-US')}`;
}

type Tone = 'plain' | 'warn' | 'risk';

interface Fact {
  label: string;
  value: string;
  suffix?: string;
  tone: Tone;
}

/**
 * The six numbers a reader needs before anything else, in two groups of three: what the
 * campaign earns per day and per month, what the issues below are estimated to cost, and then
 * the three ratios those figures come from.
 *
 * The two profit figures are the same current rate at two scales, because a monthly figure
 * lands in a way a daily one does not and a daily one is what compares two campaigns of
 * different sizes. They are actual profit, never a gap to target: a campaign making money but
 * sitting under goal shows what it makes, and the target sits three boxes along for the reader
 * to compare against rather than having the subtraction done for them.
 *
 * Profit and ROAS are the same calculation (profit is spend x (roas / breakeven - 1)), so they
 * have to share a window or the strip contradicts itself for anyone who checks the arithmetic.
 * Both are the last 30 days, the same window the collector judges "losing money" on. The
 * day-by-day read lives in the chart directly beneath.
 */
function facts(item: ActionItem, priced: number[], totalIssues: number): Fact[] {
  const econ = item.card?.economics;
  if (!econ) return [];
  const prefix = currencyPrefix(item.store);
  const out: Fact[] = [];

  const profit = econ.monthlyProfitUsd;
  if (profit != null) {
    const tone: Tone = profit < 0 ? 'risk' : 'plain';
    out.push({ label: 'Profit/day', value: money(profit / DAYS_IN_WINDOW, prefix), tone });
    out.push({ label: 'Profit, 30d', value: money(profit, prefix), tone });
  }

  // The issues' own estimates added up, not the item's stored magnitude. Magnitude also carries
  // findings that never became one of the issues printed below it, so it cannot be attributed to
  // them, and a total that cannot be attributed to what the reader can see oversells the alarm
  // (ADR 0007, decision 4).
  //
  // Neutral ink on purpose. This is a size, not a state, and it is already inside the profit
  // figures next to it rather than on top of them, so colouring it red would imply the money
  // comes off a bottom line that has not yet absorbed it.
  //
  // A hyphen holds the box open when nothing below carries an estimate, so the six columns do
  // not reflow between one card and the next.
  const unpriced = totalIssues - priced.length;
  out.push({
    label: 'Cost of issues',
    value: priced.length > 0 ? size(priced.reduce((sum, v) => sum + v, 0), itemCurrency(item)) : '-',
    suffix: unpriced > 0 && totalIssues > 0 ? `${unpriced} unpriced` : undefined,
    tone: 'plain',
  });

  // Below break-even is always also below target, so red wins over orange. Break-even and target
  // stay neutral in every state: they are constants, and colouring them would say they moved.
  const roas = econ.roas30 ?? null;
  const breakeven = econ.breakevenRoas ?? item.breakeven_roas ?? null;
  const target = econ.targetRoas ?? item.target_roas ?? null;
  if (roas != null) {
    const tone: Tone = breakeven != null && roas < breakeven ? 'risk'
      : target != null && roas < target ? 'warn'
      : 'plain';
    out.push({ label: 'ROAS 30d', value: `${roas.toFixed(2)}x`, tone });
  }
  if (breakeven != null) out.push({ label: 'BE ROAS', value: `${breakeven.toFixed(2)}x`, tone: 'plain' });
  if (target != null) out.push({ label: 'Target ROAS', value: `${target.toFixed(2)}x`, tone: 'plain' });

  return out;
}

const TONE_COLOR: Record<Tone, string> = { plain: 'text.primary', warn: WARN, risk: RISK };

/**
 * The top block of a campaign card: six labelled numbers in a row, and nothing else. It
 * replaced a single bold sentence that led with the severity contradiction and named one issue
 * plus "and 4 more" (L, 2026-08-07), and then the muted reconciling line under that strip,
 * which restated in prose what the boxes already say (L, 2026-09-04).
 */
export default function FactStrip({ item }: { item: ActionItem }) {
  const issues = item.card?.issues ?? [];
  const priced = issues.filter(i => i.dollars != null).map(i => i.dollars!);
  const rows = facts(item, priced, issues.length);
  if (rows.length === 0) return null;

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(${rows.length}, minmax(0, 1fr))`,
      borderTop: `1px solid ${BORDER}`,
      borderBottom: `1px solid ${BORDER}`,
      fontVariantNumeric: 'tabular-nums',
      '@media (max-width: 900px)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
      '@media (max-width: 520px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    }}>
      {rows.map(fact => (
        <Box key={fact.label} sx={{
          px: 1.5, pt: '11px', pb: '12px',
          borderLeft: `1px solid ${BORDER}`,
          '&:first-of-type': { borderLeft: 0, pl: 0 },
        }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', lineHeight: 1.25, textTransform: 'uppercase', color: 'text.secondary', mb: '4px', whiteSpace: 'nowrap' }}>
            {fact.label}
          </Typography>
          <Typography sx={{ fontSize: 19, fontWeight: 700, letterSpacing: '-0.01em', color: TONE_COLOR[fact.tone], whiteSpace: 'nowrap' }}>
            {fact.value}
            {/* The count of unpriced issues rides inline with the total rather than under it,
                so the box stays one line high like the other five. Without it the figure reads
                as the cost of everything listed below, which it is not. */}
            {fact.suffix && (
              <Box component="span" sx={{ ml: 0.75, fontSize: 11.5, fontWeight: 500, color: 'text.secondary' }}>{fact.suffix}</Box>
            )}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
